import { useCurrentUserCtx } from 'client/contexts/CurrentUser';
import { AnchorAPI } from 'client/modules/AnchorAPI';
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import * as Sentry from '@sentry/browser';
import { trackEvent } from 'modules/analytics';

const FETCH_PARTNER_IDS_KEY = 'fetchPartnerIds';

export function usePartnerIds() {
  const queryClient = useQueryClient();
  const {
    state: { userId },
  } = useCurrentUserCtx();

  const { data } = useQuery(
    [FETCH_PARTNER_IDS_KEY, userId],
    () => AnchorAPI.fetchPartnerIds(userId!),
    {
      enabled: !!userId,
    }
  );
  const { mparticle } = data || {};

  /**
   * tracks if we need to fire the onboarding event after sign up
   * the state will only be set to 'pending' after the user clicks the sign up CTA
   */
  const [trackOnboarding, setTrackOnboarding] = useState<
    'idle' | 'pending' | 'complete' | 'error'
  >('idle');

  /**
   * Track the onboarding event when the user has completed the onboarding flow
   */
  useEffect(() => {
    // `trackOnboarding` will be set to 'pending' when a user has sucessfully submitted the sign up form
    if (userId && mparticle && trackOnboarding === 'pending') {
      /**
       * Upon fetching IDs after signup, we must await mParticle.EventType
       * and customerid before we can track the 'onboarding_completed' event.
       * These properties becomes available after initializeWithUser() is
       * called in our App component's useEffect hook.
       */
      let attempts = 0;
      const trackOnboardingCompleted = (maxAttempts = 50) => {
        attempts = attempts + 1;

        if (
          typeof mParticle !== 'undefined' &&
          mParticle.EventType &&
          mParticle.Identity.getCurrentUser?.()?.getUserIdentities()
            .userIdentities.customerid
        ) {
          trackEvent(
            'onboarding_completed',
            { type: 'email' },
            { providers: [mParticle] }
          );
          setTrackOnboarding('complete');
          return;
        }
        if (attempts === maxAttempts) {
          Sentry.captureException(
            new Error(
              'Unable to track onboarding_completed event (exceeded max attempts)'
            )
          );
          setTrackOnboarding('error');
          return;
        }
        setTimeout(trackOnboardingCompleted, 1000);
      };
      setTimeout(trackOnboardingCompleted, 1000);
    }
  }, [userId, mparticle, trackOnboarding]);

  function resetPartnerIds() {
    queryClient.removeQueries([FETCH_PARTNER_IDS_KEY]);
  }

  return { data, setTrackOnboarding, resetPartnerIds };
}
